import React, { useEffect, useState } from 'react';
import { confirmDialog } from '../components/ui/ImpulzaDialog';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync, asi que cada app conserva su brandName sin
// hardcodear "Impulza" aca.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  AlertTriangle,
  Archive,
  Ban,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Layers,
  Package,
  Printer,
  RefreshCcw,
  Search,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/numberFormat';

const getUserLabel = (userRow) => userRow?.full_name || userRow?.email || 'Usuario';

// FIX IMPORTANTE #19: paginación server-side. Antes traíamos TODO el historial de la org.
const PAGE_SIZE = 50;

function ProduccionHistorial({ onNavigate }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [profilesMap, setProfilesMap] = useState(new Map());
  const [showModal, setShowModal] = useState(false);
  const [ordenAAnular, setOrdenAAnular] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('Carga incorrecta');

  // FIX IMPORTANTE #19: filtros server-side
  const [page, setPage] = useState(0);
  const [sitios, setSitios] = useState([]);
  const [filtroSitioId, setFiltroSitioId] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos'); // todos | activos | anulados
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [debouncedBusqueda, setDebouncedBusqueda] = useState('');

  // Debounce para la búsqueda (no disparar query por cada tecla)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBusqueda(busqueda), 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  useEffect(() => {
    if (profile?.organization_id) {
      fetchSitios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  // Cuando cambia cualquier filtro, volvemos a página 0 y recargamos
  useEffect(() => {
    if (profile?.organization_id) {
      setPage(0);
      fetchHistorial(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profile?.organization_id,
    filtroSitioId,
    filtroEstado,
    filtroDesde,
    filtroHasta,
    debouncedBusqueda,
  ]);

  // Cuando cambia la página (sin tocar filtros) recargamos
  useEffect(() => {
    if (profile?.organization_id && page > 0) {
      fetchHistorial(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchSitios = async () => {
    const { data } = await supabase
      .from('sitios')
      .select('id, nombre')
      .eq('organization_id', profile.organization_id)
      .order('nombre');
    setSitios(data || []);
  };

  const fetchHistorial = async (pageNumber = 0) => {
    try {
      setLoading(true);
      const from = pageNumber * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('produccion')
        .select(`
          *,
          sitios:sitio_id (nombre)
        `, { count: 'exact' })
        .eq('organization_id', profile.organization_id);

      // Filtro por estado
      if (filtroEstado === 'activos') query = query.eq('activo', true);
      if (filtroEstado === 'anulados') query = query.eq('activo', false);

      // Filtro por sitio
      if (filtroSitioId) query = query.eq('sitio_id', filtroSitioId);

      // Filtro por rango de fecha
      if (filtroDesde) query = query.gte('fecha', filtroDesde);
      if (filtroHasta) {
        // Sumamos un día para incluir el "hasta" completo
        const hasta = new Date(filtroHasta);
        hasta.setDate(hasta.getDate() + 1);
        query = query.lt('fecha', hasta.toISOString().split('T')[0]);
      }

      // Búsqueda por nombre de producto u observaciones (case-insensitive)
      if (debouncedBusqueda?.trim()) {
        const term = `%${debouncedBusqueda.trim()}%`;
        query = query.or(`producto_nombre.ilike.${term},observaciones.ilike.${term}`);
      }

      const { data, error, count } = await query
        .order('fecha', { ascending: false })
        .range(from, to);

      if (error) throw error;

      const rows = data || [];
      setOrdenes(rows);
      setTotalCount(count || 0);

      const profileIds = Array.from(
        new Set(rows.flatMap((row) => [row.created_by, row.anulado_por]).filter(Boolean)),
      );

      if (profileIds.length > 0) {
        const profilesRes = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', profileIds);

        if (profilesRes.error) throw profilesRes.error;
        setProfilesMap(new Map((profilesRes.data || []).map((row) => [row.id, row])));
      } else {
        setProfilesMap(new Map());
      }
    } catch (error) {
      toast.error(`Error al cargar historial: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (orden) => {
    if (typeof onNavigate === 'function') {
      onNavigate('produccion-historial', orden.id);
      return;
    }
    navigate(`/sistema/produccion-historial/${orden.id}`);
  };

  // FIX CRÍTICO #9: helper para escapar HTML y evitar XSS en ventanas de impresión.
  // Antes se interpolaban valores de la DB directamente en document.write, lo que permitía
  // que un nombre de producto con `<script>` ejecutara código en la ventana de impresión.
  const escapeHTML = (value) => {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const handlePrint = (event, orden) => {
    event.stopPropagation();
    const ventana = window.open('', '_blank');
    if (!ventana) return; // popup bloqueado
    const idCorto = escapeHTML(String(orden.id || '').slice(0, 8));
    const productoNombre = escapeHTML(orden.producto_nombre || '');
    const cantidad = escapeHTML(orden.cantidad || 0);
    const fecha = escapeHTML(orden.fecha ? new Date(orden.fecha).toLocaleString() : '');
    const sitio = escapeHTML(orden.sitios?.nombre || 'General');
    const filasInsumos = (orden.ingredientes_usados || [])
      .map((ing) => `
        <tr>
          <td>${escapeHTML(ing.nombre || 'Insumo')}</td>
          <td>${escapeHTML(ing.necesario || 0)} ${escapeHTML(ing.um || '')}</td>
        </tr>
      `)
      .join('');

    ventana.document.write(`
      <html>
        <head>
          <title>Orden de Produccion #${idCorto}</title>
          <style>
            body { font-family: sans-serif; padding: 40px; color: #333; }
            .header { border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: bold; text-transform: uppercase; }
            .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .label { font-size: 10px; color: #888; text-transform: uppercase; font-weight: bold; }
            .value { font-size: 14px; font-weight: bold; margin-top: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { text-align: left; font-size: 10px; text-transform: uppercase; color: #888; border-bottom: 1px solid #eee; padding: 10px; }
            td { padding: 10px; border-bottom: 1px solid #f9f9f9; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">Comprobante de Produccion</div>
            <p>${BRAND_NAME} Enterprise - Gestion de Fabrica</p>
          </div>
          <div class="meta">
            <div><div class="label">Producto</div><div class="value">${productoNombre}</div></div>
            <div><div class="label">Cantidad</div><div class="value">${cantidad}</div></div>
            <div><div class="label">Fecha</div><div class="value">${fecha}</div></div>
            <div><div class="label">Sitio</div><div class="value">${sitio}</div></div>
          </div>
          <div class="label">Insumos Utilizados</div>
          <table>
            <thead><tr><th>Ingrediente</th><th>Cantidad</th></tr></thead>
            <tbody>
              ${filasInsumos}
            </tbody>
          </table>
        </body>
      </html>
    `);
    ventana.document.close();
    ventana.print();
  };

  const handleOpenAnularModal = (event, orden) => {
    event.stopPropagation();
    setOrdenAAnular(orden);
    setMotivoAnulacion('Carga incorrecta');
    setShowModal(true);
  };

  const handleAnular = async () => {
    if (!ordenAAnular) return;

    // FIX UX #15: confirmación detallada con info concreta del impacto.
    // Avisamos cuántas unidades se retrotraen y si hay prorrateos asociados
    // (que podrían quedar inconsistentes tras la reversa).
    const tieneProrrateos = String(ordenAAnular.observaciones || '').includes('[Prorrateo:');
    const cantidadAnular = Number(ordenAAnular.cantidad || 0);
    const advertencias = [];
    advertencias.push(`Se retrotraerán ${cantidadAnular} unidad(es) de "${ordenAAnular.producto_nombre}".`);
    advertencias.push(`Stock destino actualizado, insumos devueltos al origen.`);
    if (tieneProrrateos) {
      advertencias.push('⚠️  Esta orden tiene prorrateos aplicados. Considerá revisarlos en Distribución antes de anular.');
    }
    if (!ordenAAnular.activo) {
      toast.error('La orden ya está anulada.');
      return;
    }
    const ok = await confirmDialog({
      title: '¿Anular la producción?',
      tone: 'danger',
      details: [{ label: 'Motivo', value: String(motivoAnulacion || '').trim() || 'Carga incorrecta' }],
      list: { items: advertencias.map((a) => a.replace(/^⚠️\s*/, '')) },
      confirmLabel: 'Anular',
    });
    if (!ok) return;

    const toastId = toast.loading('Procesando anulacion y retrotrayendo movimientos...');

    try {
      const rpcRes = await supabase.rpc('reverse_production_entry', {
        p_production_id: ordenAAnular.id,
        p_reason: String(motivoAnulacion || '').trim() || 'Carga incorrecta',
      });

      if (rpcRes.error) throw rpcRes.error;

      const warnings = Array.isArray(rpcRes.data?.warnings) ? rpcRes.data.warnings.filter(Boolean) : [];
      if (warnings.length > 0) {
        toast(`Produccion anulada con advertencias: ${warnings.join(' ')}`, { id: toastId, icon: '!', duration: 7000 });
      } else {
        toast.success('Produccion anulada y stock retrotraido.', { id: toastId });
      }
      setShowModal(false);
      setOrdenAAnular(null);
      await fetchHistorial(page);
    } catch (error) {
      toast.error(`Error al anular: ${error.message}`, { id: toastId });
    }
  };

  // FIX IMPORTANTE #19: filtrado YA es server-side, ordenesFiltradas es solo el alias.
  const ordenesFiltradas = ordenes;

  // KPIs: ahora reflejan el TOTAL filtrado en server (count), no solo la página visible.
  const kpis = {
    total: totalCount,
    activas: ordenes.filter((orden) => orden.activo).length, // página visible
    anuladas: ordenes.filter((orden) => !orden.activo).length, // página visible
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const goPrev = () => setPage((p) => Math.max(0, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  // FIX IMPORTANTE #22: respeta locale/currency de la organización
  const fmtMoney = (value) => formatCurrency(value, { orgId: profile?.organization_id });

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <FileText size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
              Historial de Produccion
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Auditoria, anulaciones blandas y reversa de stock.
            </p>
          </div>
        </div>
      </div>

      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Registros</p>
            <Layers size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Movimientos totales</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Producciones OK</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.activas}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Activas en stock</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Anuladas</p>
            <Ban size={16} className="text-slate-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${kpis.anuladas > 0 ? 'text-rose-500' : 'text-slate-300'}`}>
            {kpis.anuladas}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Con reversa registrada</p>
        </div>
      </div>

      {/* FIX IMPORTANTE #19: filtros server-side */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2">
          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1 mb-1">
            <Search size={10} /> Buscar
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="BUSCAR POR PRODUCTO U OBSERVACIONES..."
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-bold outline-none focus:border-[#51A7FD] uppercase text-slate-700"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={14} />
          </div>
        </div>
        <div>
          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Sitio</label>
          <select
            value={filtroSitioId}
            onChange={(e) => setFiltroSitioId(e.target.value)}
            className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2 px-3 text-[10px] font-bold uppercase outline-none"
          >
            <option value="">Todos</option>
            {sitios.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Estado</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2 px-3 text-[10px] font-bold uppercase outline-none"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activas</option>
            <option value="anulados">Anuladas</option>
          </select>
        </div>
        <div>
          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Fecha desde</label>
          <input
            type="date"
            value={filtroDesde}
            onChange={(e) => setFiltroDesde(e.target.value)}
            className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2 px-3 text-[10px] font-bold outline-none"
          />
        </div>
        <div>
          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 block">Fecha hasta</label>
          <input
            type="date"
            value={filtroHasta}
            onChange={(e) => setFiltroHasta(e.target.value)}
            className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2 px-3 text-[10px] font-bold outline-none"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Archive size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">Bitacora de operaciones</span>
          </div>
          <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">
            {ordenesFiltradas.length} de {totalCount} registros
          </span>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-[#51A7FD] rounded-full animate-spin"></div>
            <p className="mt-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Cargando Historial...</p>
          </div>
        ) : ordenesFiltradas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-300 opacity-50">
            <Package size={40} strokeWidth={1} className="mb-2" />
            <p className="text-[9px] font-black uppercase tracking-[0.2em]">Sin registros encontrados</p>
          </div>
        ) : (
          <div className="overflow-x-auto imp-table-wrap">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="py-3 px-5">Fecha / Planta</th>
                  <th className="py-3 px-4">Producto Elaborado</th>
                  <th className="py-3 px-4 text-center">Cantidad</th>
                  <th className="py-3 px-4 text-center">Estado</th>
                  <th className="py-3 px-4">Auditoria</th>
                  <th className="py-3 px-4 text-right">Costo Lote</th>
                  <th className="py-3 px-5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {ordenesFiltradas.map((orden) => (
                  <tr
                    key={orden.id}
                    onClick={() => handleOpenDetail(orden)}
                    className={`hover:bg-blue-50/20 transition-all cursor-pointer group ${!orden.activo ? 'opacity-70 bg-slate-50/50' : ''}`}
                  >
                    <td className="py-3 px-5">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5 text-[10px] font-black text-slate-700 uppercase">
                          <Calendar size={10} className="text-slate-300" />
                          {new Date(orden.fecha).toLocaleString()}
                        </div>
                        <span className="text-[8px] text-slate-400 font-bold uppercase ml-4">
                          {orden.sitios?.nombre || 'Planta Central'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Package size={14} className="text-slate-300" />
                        <div>
                          <span className="text-[10px] font-black uppercase text-slate-800 tracking-tight block">
                            {orden.producto_nombre}
                          </span>
                          {orden.observaciones ? (
                            <span className="text-[8px] font-bold uppercase text-slate-400 block mt-1">
                              {orden.observaciones}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded-lg text-[10px] font-black border border-slate-200">
                        {orden.cantidad} u.
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {orden.activo ? (
                        <span className="text-[8px] font-black text-emerald-600 uppercase bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100 flex items-center justify-center gap-1 w-fit mx-auto">
                          <CheckCircle2 size={10} /> OK
                        </span>
                      ) : (
                        <span className="text-[8px] font-black text-rose-500 uppercase bg-rose-50 px-2 py-1 rounded-lg border border-rose-100 flex items-center justify-center gap-1 w-fit mx-auto">
                          <Ban size={10} /> Anulado
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-[8px] font-bold uppercase tracking-widest text-slate-400 space-y-1">
                        <p>Cargo: {getUserLabel(profilesMap.get(orden.created_by))}</p>
                        {!orden.activo ? (
                          <p>Anulo: {getUserLabel(profilesMap.get(orden.anulado_por))}</p>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-[10px] font-black text-slate-700">{fmtMoney(orden.costo_total)}</span>
                    </td>
                    <td className="py-3 px-5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(event) => handlePrint(event, orden)}
                          className="p-2 bg-white border border-slate-200 text-slate-400 hover:text-[#51A7FD] hover:border-blue-200 rounded-xl transition-all shadow-sm"
                          title="Imprimir Comprobante"
                        >
                          <Printer size={14} />
                        </button>
                        {orden.activo ? (
                          <button
                            onClick={(event) => handleOpenAnularModal(event, orden)}
                            className="p-2 bg-white border border-slate-200 text-slate-400 hover:text-rose-500 hover:border-rose-200 rounded-xl transition-all shadow-sm"
                            title="Anular Produccion"
                          >
                            <XCircle size={14} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* FIX IMPORTANTE #19: paginación server-side */}
        {totalCount > PAGE_SIZE && (
          <div className="bg-slate-50 border-t border-slate-100 p-3 flex items-center justify-between">
            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">
              Página {page + 1} de {totalPages} · {totalCount} registros totales
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goPrev}
                disabled={page === 0 || loading}
                className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft size={12} /> Anterior
              </button>
              <button
                type="button"
                onClick={goNext}
                disabled={page >= totalPages - 1 || loading}
                className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-[#51A7FD] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Siguiente <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0B2A36]/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-white/20">
            <div className="h-14 w-14 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mb-5 mx-auto border border-rose-100 shadow-sm">
              <AlertTriangle size={28} />
            </div>
            <h3 className="text-lg font-black text-center text-slate-800 uppercase tracking-tight mb-2">Anular Produccion</h3>
            <p className="text-[10px] text-slate-500 text-center mb-4 font-bold uppercase leading-relaxed px-4">
              Se retrotrae el stock y queda el log de quien hizo la anulacion.
            </p>
            <textarea
              value={motivoAnulacion}
              onChange={(event) => setMotivoAnulacion(event.target.value)}
              className="w-full h-24 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold text-slate-700 outline-none focus:border-[#51A7FD] resize-none"
              placeholder="Motivo de anulacion"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-white border border-slate-200 text-slate-500 rounded-2xl font-black text-[9px] uppercase tracking-widest hover:bg-slate-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleAnular}
                className="flex-1 py-3 bg-rose-500 text-white rounded-2xl font-black text-[9px] uppercase tracking-widest hover:bg-rose-600 shadow-lg shadow-rose-500/20 transition-all"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProduccionHistorial;
